import CrossIcon from '../icons/CrossIcon';
import DateRangePickerComponent from '../date-range-picker/DateRangePicker';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Modal as MUIModal, styled, TextField } from '@mui/material';
import { fontWeight } from '../../theme/typography';
import { palette } from '../../theme/palette';
import { PrimaryButton } from '../Buttons';
import { BodyLarge, BodyGrey } from '../Typography';
import { HFlex, VFlex } from '../FlexContainers';

export const Modal = styled(MUIModal)``;

export const ModalContent = styled(Box)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80%;
  max-width: 34rem;
  border-radius: 5px;
  box-shadow:
    0px 8px 15px 0px rgba(0, 0, 0, 0.1),
    0px -8px 15px 0px rgba(0, 0, 0, 0.1);
  background: ${palette.primary.contrastText};
  flex-shrink: 0;
  :focus-visible {
    outline: 0px;
  }
`;

export const ContentWrapper = styled(Box)`
  position: relative;
  max-height: 90vh;
  overflow-y: auto;
  padding: 3rem;
  box-sizing: border-box;
  :focus-visible {
    outline: 0px;
  }
`;

export const BottomSheetTopBar = styled(HFlex)`
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  margin-bottom: 15px;
  padding: 0px 20px;
`;

export const BottomSheetLabel = styled(BodyLarge)`
  font-weight: ${fontWeight.bold};
  margin-bottom: 0px;
`;

export const BottomSheetContentWrapper = styled(Box)`
  padding: 0px 20px 30px 20px;
`;

export const CloseIcon = styled(CrossIcon)`
  color: ${palette.custom.grey};
  width: 21px;
  height: 21px;
  object-fit: contain;
`;

export const CloseButton = styled(Box)`
  height: 25px;
  width: 25px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${palette.primary.main};
  position: absolute;
  top: 16px;
  right: 18px;
`;

export const BottomSheetCloseButton = styled(Box)`
  height: 25px;
  width: 25px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${palette.primary.main};
`;

export const Title = styled('h2')`
  font-family: trade-gothic-next-condensed, sans-serif;
  font-size: 24px;
  font-weight: ${fontWeight.heavy};
  color: ${palette.custom.darkGrey};
  margin: 0 0 24px 0;
  line-height: 30px;
  padding-right: 30px;
`;

export const Subtitle = styled(BodyLarge)`
  margin-bottom: 12px;
`;

export const Label = styled(BodyLarge)`
  margin-bottom: 5px;
`;

export const HelperText = styled(BodyGrey, {
  shouldForwardProp: (prop) => prop !== 'hasError',
})<{
  hasError?: boolean;
}>`
  margin-top: 5px;
  margin-left: 3px;
  color: ${(props) => (props.hasError ? palette.error.main : palette.custom.mediumGrey)};
  width: fit-content;
`;

export const DateRangePicker = styled(DateRangePickerComponent, {
  shouldForwardProp: (prop) => prop !== 'startDateError' && prop !== 'endDateError',
})<{
  startDateError?: boolean;
  endDateError?: boolean;
}>`
  .date-picker-start-date-container .date-range-picker-field .MuiInputBase-root {
    border-color: ${(props) =>
      props.startDateError ? `${palette.error.main}` : `${palette.custom.grey}`};
  }
  .date-picker-end-date-container .date-range-picker-field .MuiInputBase-root {
    border-color: ${(props) =>
      props.endDateError ? `${palette.error.main}` : `${palette.custom.grey}`};
  }
  .date-picker-start-date-container .date-range-picker-field .MuiInputBase-input,
  .date-picker-end-date-container .date-range-picker-field .MuiInputBase-input {
    font-size: 16px;
  }
`;

export const GetEstimateButton = styled(PrimaryButton)`
  width: 100%;
  height: 48px;
  margin-top: 28px;
  font-size: 16px;
  letter-spacing: 0.8px;

  &:disabled {
    background-color: ${palette.custom.grey};
    border-color: ${palette.custom.grey};
    cursor: not-allowed;
    pointer-events: auto;

    &:hover {
      background-color: ${palette.custom.grey};
      border-color: ${palette.custom.grey};
    }
  }
`;

// Same-Day Notice Banner

export const SameDayNotice = styled(HFlex)`
  gap: 12px;
  align-items: flex-start;
  background: ${palette.custom.warningBackground};
  border: 1px solid ${palette.custom.warningBorder};
  border-radius: 5px;
  padding: 12px 16px;
  margin-top: 20px;
`;

export const SameDayNoticeIcon = styled(WarningAmberIcon)`
  color: ${palette.primary.main};
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  margin-top: 1px;
`;

export const SameDayNoticeText = styled(BodyLarge)`
  margin-bottom: 0;
  font-size: 14px;
  line-height: 20px;
`;

// Step 2: Rate Card

export const RateCard = styled(Box)`
  border: 0.75px solid ${palette.custom.grey};
  border-radius: 5px;
  padding: 18px 22px;
  margin-bottom: 20px;
`;

export const RateRow = styled(HFlex)`
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
`;

export const BestRateLabel = styled('span')`
  font-size: 16px;
  font-weight: ${fontWeight.bold};
  color: ${palette.custom.darkGrey};
`;

export const BestRateValue = styled('span')`
  font-size: 20px;
  font-weight: ${fontWeight.bold};
  color: ${palette.primary.main};
`;

export const StandardRateValue = styled('span')`
  font-size: 16px;
  color: ${palette.custom.mediumGrey};
  text-decoration: line-through;
`;

export const RentalDuration = styled('span')`
  font-size: 14px;
  color: ${palette.custom.mediumGrey};
`;

export const SavingsMessageBox = styled(Box)`
  border: 1.5px dashed ${palette.custom.grey};
  border-radius: 5px;
  padding: 10px 14px;
  margin-top: 12px;
`;

export const SavingsMessageText = styled(BodyLarge)`
  margin: 0;
  font-size: 14px;
  line-height: 20px;
`;

export const ChangeDatesLink = styled('a')`
  font-size: 14px;
  color: ${palette.primary.main};
  cursor: pointer;
  text-decoration: underline;
  margin-top: 6px;
  display: inline-block;
`;

export const Disclaimer = styled('p')`
  font-size: 12px;
  color: ${palette.custom.mediumGrey};
  margin: 8px 0 0 0;
  line-height: 18px;
`;

// Step 2: Contact Form

export const ContactSection = styled(VFlex)`
  gap: 16px;
`;

export const ContactHeading = styled('h3')`
  font-family: trade-gothic-next, sans-serif;
  font-size: 16px;
  font-weight: ${fontWeight.bold};
  color: ${palette.custom.darkGrey};
  margin: 0 0 6px 0;
  line-height: 22px;
`;

export const ContactSubtext = styled(BodyLarge)`
  margin-bottom: 0;
  font-size: 14px;
  line-height: 20px;
`;

export const BranchPhoneLink = styled('a')`
  color: ${palette.primary.main};
  font-weight: ${fontWeight.bold};
  text-decoration: none;
`;

export const FormField = styled(TextField)`
  width: 100%;

  .MuiInputBase-root {
    border-radius: 5px;
    height: 48px;
  }

  .MuiInputBase-input {
    font-size: 16px;
    line-height: 24px;
  }

  .MuiInputBase-input::placeholder {
    color: ${palette.custom.mediumGrey};
    opacity: 1;
  }

  .MuiOutlinedInput-notchedOutline {
    border-color: ${palette.custom.grey};
  }

  .Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${palette.primary.main} !important;
  }
`;

export const ValidationMessage = styled('p')`
  font-size: 14px;
  color: ${palette.error.main};
  margin: 0;
  line-height: 20px;
`;

export const RequestRentalButton = styled(PrimaryButton)`
  width: 100%;
  height: 48px;
  font-size: 16px;
  letter-spacing: 0.8px;
`;

// Inline missing-price message

export const MissingPriceMessageWrapper = styled(Box)`
  width: 100%;
  padding: 8px 16px;
  background: ${palette.custom.lightGrey};
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 28px;
  margin-bottom: 0px;
  text-align: center;
`;

export const MissingPriceMessage = styled(BodyLarge)`
  display: inline;
  margin-bottom: 0;
`;

export const MissingPriceMessagePhoneLink = styled('a')`
  letter-spacing: 0.35px;
  color: ${palette.custom.darkGrey};
  margin-bottom: 0px;
  width: fit-content;
  font-size: 16px;
  font-weight: 700;
  text-decoration: none;
`;

export const ContinueLoaderSlot = styled('span')`
  display: inline-flex;
  align-items: center;
  margin-left: 12px;
  vertical-align: middle;
`;

// Thank You Modal

export const ThankYouMessageBox = styled(Box)`
  background: ${palette.custom.grey100};
  border-radius: 4px;
  padding: 16px 20px;
`;

export const ThankYouMessageText = styled(BodyLarge)`
  margin: 0;
  line-height: 24px;
  text-align: center;
`;
